import React, { useEffect, useState } from "react";
import AxiosInstance from "../../../utils/axiosInstance";
import Pagination from "../../../components/Layout/Pagination";
import SpecialitiesFilters from "./SpecialitiesFilters";
import WorkTypeFilter from "../../../components/Filters/WorkTypeFilter";
import paginationValue from "../../../common/paginationValue.json";
import loader from "../../../assets/loader.svg";
import axios from "axios";
import { toast } from "react-hot-toast";

export default function SpecialitiesMappedList({ onRowClick, refetchTrigger }) {
    const [currentPage, setCurrentPage] = useState(paginationValue.currentPage);
    const [resetTrue, setResetTrue] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageLimit, setPageLimit] = useState(paginationValue.limit);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filterSpeciality, setFilterSpeciality] = useState(null);
    const [filterHighway, setFilterHighway] = useState(null);
    const [filterBuilding, setFilterBuilding] = useState(null);

    // flag to skip API fetch when Reset button is clicked
    const [skipFetch, setSkipFetch] = useState(false);

    const handleSpecialityChange = (speciality) => setFilterSpeciality(speciality);
    const handleHighwayChange = (highway) => setFilterHighway(highway);
    const handleBuildingChange = (building) => setFilterBuilding(building);

    const getSpecialityMappingList = async (controller) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: pageLimit,
                speciality: filterSpeciality || "",
                highway: filterHighway || "",
                building: filterBuilding || "",
                ts: new Date().toISOString() // add timestamp
            }).toString();

            const response = await AxiosInstance.get(
                `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_GET_SPECIALITY_MAPPING_DATA}?${queryParams}`,
                { signal: controller.signal }
            );

            const data = response.data;
            if (data.statusCode === 200) {
                setTableData(data.data || []);
                setTotalPages(data.total_pages || 1);
                setTotalRecords(data.total_records || 0);
            } else {
                setTableData([]);
                setTotalPages(1);
                setTotalRecords(0);
                toast.info(data.message || "No records found");
            }
        } catch (error) {
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                console.log("Request canceled");
            } else {
                console.error("Error fetching speciality mapping list:", error);
                setTableData([]);
                setTotalPages(1);
                setTotalRecords(0);
                toast.error(error?.message || "Something went wrong");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (skipFetch) return;

        const controller = new AbortController();

        // ✅ If any dependency (except refetchTrigger) changes, reset page to 1
        // We compare the last dependencies to current to detect changes
        // Simplest approach: if the change is NOT from refetchTrigger, set page=1
        // and only call API after that set is done.
        getSpecialityMappingList(controller);

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentPage,
        resetTrue,
        filterSpeciality,
        filterHighway,
        filterBuilding,
        refetchTrigger,
        skipFetch
    ]);

    // ✅ Reset page to 1 whenever filters or Search change, but not for refetchTrigger
    useEffect(() => {
        // Each time filters or Search toggles, reset to page 1
        // We deliberately exclude refetchTrigger from the deps so that
        // an external refetch won't reset the page.
        setCurrentPage(1);
    }, [filterSpeciality, filterHighway, filterBuilding, resetTrue]);

    const handlePageChange = (page) => setCurrentPage(page);

    const handleReset = () => {
        setSkipFetch(true);
        setCurrentPage(1);
        setFilterSpeciality(null);
        setFilterHighway(null);
        setFilterBuilding(null);
        setTableData([]);
        setTotalPages(1);
        setTotalRecords(0);
        setResetTrue(!resetTrue);

        setTimeout(() => setSkipFetch(false), 0);
    };

    return (
        <div className="col-12 pb-4">
            <div className="card w-100 h-100 bg-white rounded-3 shadow-sm mb-4 border-0">
                <div className="card-header g-0 m-0 p-0 overflow-hidden border-bottom border-light border-2">
                    <div className="row g-0">
                        <div className="col g-0">
                            <h3 className="p-3 mb-0 pb-0 point-22 text-uppercase text-primary fw-bold">
                                Specialities Mapped List
                            </h3>
                        </div>
                    </div>
                </div>

                <div className="card-body p-3">
                    {/* Filters */}
                    <div className="row g-2 mb-3">
                        <div className="col-12 col-md-auto">
                            <span className="input-group-text ps-0 ms-0 text-uppercase text-secondary fw-bold border-0 bg-white rounded-0">
                                Filter:
                            </span>
                        </div>

                        <div className="col-12 col-md">
                            <SpecialitiesFilters
                                onSelectSpecilities={handleSpecialityChange}
                                reset={resetTrue}
                            />
                        </div>

                        <div className="col-12 col-md">
                            <WorkTypeFilter
                                placeholder="Select Highway"
                                onSelectWorkType={handleHighwayChange}
                                reset={resetTrue}
                            />
                        </div>

                        <div className="col-12 col-md">
                            <WorkTypeFilter
                                placeholder="Select Building"
                                onSelectWorkType={handleBuildingChange}
                                reset={resetTrue}
                                isBuildingFilter
                            />
                        </div>

                        <div className="col-6 col-md-auto">
                            <button
                                className="btn btn-primary text-uppercase fw-bold rounded-pill w-100"
                                onClick={() => setResetTrue(!resetTrue)}
                            >
                                Search
                            </button>
                        </div>

                        <div className="col-6 col-md-auto">
                            <button
                                className="btn btn-link text-uppercase text-danger text-start w-100"
                                onClick={handleReset}
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                        {loading ? (
                            <div className="text-center my-5">
                                <img src={loader} alt="Loading..." width="60" />
                            </div>
                        ) : (
                            <table className="table table-bordered table-striped align-middle text-center">
                                <thead className="table-light">
                                    <tr>
                                        <th>Speciality</th>
                                        <th>Highway</th>
                                        <th>Building</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.length > 0 ? (
                                        tableData.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className="cursor-pointer"
                                                onClick={() =>
                                                    onRowClick(row.speciality?.id)
                                                }
                                            >
                                                <td>{row.speciality?.name}</td>
                                                <td>{row.highway}</td>
                                                <td>{row.building}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3">No records found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {!loading && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            pageLimit={pageLimit}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
